/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
  Component,
  createSignal,
  createEffect,
  For,
  Show,
  onMount,
  untrack,
} from 'solid-js';

// Tag model interfaces (reused from original)
export enum Separators {
  ENTER = '13',
  COMMA = '44',
  SPACE = '32',
  PIPE = '124',
  MINUS = '45',
  SLASH = '47',
  SEMICOLON = '59',
}

export interface ITagItem {
  label: string;
  value: string;
  isValid: boolean;
  id?: string;
  color?: string;
  tiduuid?: string;
}

export enum SearchBy {
  LABEL = 'LABEL',
  VALUE = 'VALUE',
  BOTH = 'BOTH',
}

export interface IInputCount {
  selectedCount?: number;
  totalCount: number;
}

export interface TagEditorProps {
  separator?: Separators[];
  selectedTagList?: ITagItem[];
  matchCase?: boolean;
  title?: string;
  placeHolder?: string;
  validateRx?: RegExp;
  invalidErrorMsg?: string;
  notFoundErrorMsg?: string;
  tagList?: ITagItem[];
  searchBy?: SearchBy;
  allowCustomTag?: boolean;
  setFocus?: boolean;
  id?: string;
  customErrorMessage?: string;
  matchBy?: SearchBy;
  autoSave?: boolean;
  enableSearchIcon?: boolean;
  predefinedTagList?: ITagItem[];
  tagListTitle?: string;
  maxLength?: number;
  inputCount?: IInputCount;
  limitErrorMessage?: string;
  // Callback props for event handling
  onTagChange?(tags: ITagItem[]): void;
  onTypeText?(query: string): void;
  onSearchClose?(): void;
}

// Simple InputChips component for SolidJS
const InputChips: Component<{
  tags: ITagItem[];
  onRemoveTag(index: number): void;
}> = (props) => {
  return (
    <For each={props.tags}>
      {(tag, index) => (
        <li class={`chips ${!tag.isValid ? 'error' : ''}`}>
          <span title={tag.label}>{tag.label}</span>
          <button
            type="button"
            class="button tertiary icon-small icon-circle"
            onClick={() => {
              props.onRemoveTag(index());
            }}
          >
            <i class="icon-font tc-icon-cancel i16" />
          </button>
        </li>
      )}
    </For>
  );
};

// Simple TagItems component for SolidJS
const TagItems: Component<{
  tags: ITagItem[];
  activeIndex: number;
  search: string;
  onAddTag(tag: ITagItem): void;
  isLoading: boolean;
  notFoundErrorMsg?: string;
  matchCase?: boolean;
  enableSearchIcon?: boolean;
  tagListTitle?: string;
}> = (props) => {
  const highlightText = (source: string, key: string, matchCase = true) => {
    if (!key) return source;
    const falseSource = !matchCase ? source.toLowerCase() : source;
    const falseKey = !matchCase ? key.toLowerCase() : key;
    const index = falseSource.indexOf(falseKey);
    if (index === -1) return source;

    const actualKey = source.substr(index, key.length);
    const before = source.substr(0, index);
    const after = source.substr(index + key.length);

    return (
      <>
        {before}
        <strong>{actualKey}</strong>
        {after}
      </>
    );
  };

  return (
    <div
      class={`auto-complete te-top-fix ${
        props.enableSearchIcon ? 'tags-dd-fullheigth' : ''
      }`}
    >
      <ul class="mw-100">
        <Show when={props.isLoading}>
          <li>
            <div class="select-menu-item">Loading...</div>
          </li>
        </Show>
        <Show when={!props.isLoading && props.tags.length === 0}>
          <li>
            <div class="select-menu-item">
              {props.notFoundErrorMsg || 'No results found'}
            </div>
          </li>
        </Show>
        <Show when={!props.isLoading && props.tags.length > 0}>
          <Show when={props.tagListTitle}>
            <li>
              <div class="select-menu-item">
                <h6 class="text-meta">{props.tagListTitle}</h6>
              </div>
            </li>
          </Show>
          <For each={props.tags}>
            {(tag, index) => (
              <li>
                <div
                  class={`select-menu-item ${
                    props.activeIndex === index() ? 'bg-gray_0' : ''
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onAddTag(tag);
                  }}
                >
                  <div class="text-ellipsis small" title={tag.label}>
                    {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
                    {highlightText(tag.label, props.search, props.matchCase)}
                  </div>
                </div>
              </li>
            )}
          </For>
        </Show>
      </ul>
    </div>
  );
};

const TagEditor: Component<TagEditorProps> = (props) => {
  // Reactive getters for props
  const getSeparator = () => props.separator || [Separators.ENTER];
  const getSelectedTagList = () => props.selectedTagList || [];
  const getMatchCase = () => props.matchCase ?? true;
  const getSearchBy = () => props.searchBy || SearchBy.LABEL;
  const getMatchBy = () => props.matchBy || SearchBy.VALUE;
  const getAllowCustomTag = () => props.allowCustomTag || false;
  const getNotFoundErrorMsg = () =>
    props.notFoundErrorMsg || 'No results found';

  const taglistValue = untrack(() => props.tagList) || [];

  // State signals
  const [searchKey, setSearchKey] = createSignal('');
  const [errMessage, setErrMessage] = createSignal('');
  const [loading, _setLoading] = createSignal(false);
  const [tags, setTags] = createSignal<ITagItem[]>(taglistValue || []);
  const [tagActiveIndex, setTagActiveIndex] = createSignal(-1);

  let inputRef: HTMLInputElement | undefined;

  // Focus input on mount if needed
  onMount(() => {
    if (props.setFocus && inputRef) {
      inputRef.focus();
    }
  });

  // Update tags when tagList prop changes
  createEffect(() => {
    if (props.tagList) {
      setTags(props.tagList);
    }
  });

  // Helper functions
  const getSearchFrom = (tag: ITagItem): string => {
    const matchCase = getMatchCase();
    const searchBy = getSearchBy();

    if (searchBy === SearchBy.VALUE) {
      return !matchCase ? tag.value.toLowerCase() : tag.value;
    } else if (searchBy === SearchBy.BOTH) {
      const value = tag.label + ' ' + tag.value;
      return !matchCase ? value.toLowerCase() : value;
    }
    return !matchCase ? tag.label.toLowerCase() : tag.label;
  };

  const getTagOptions = (): ITagItem[] => {
    const currentSearchKey = searchKey();
    const currentTags = tags();
    const matchCase = getMatchCase();

    // Convert search key to lowercase if case-insensitive
    const processedSearchKey = !matchCase
      ? currentSearchKey.toLowerCase()
      : currentSearchKey;

    let tagOptions = [
      ...currentTags.filter((t: ITagItem) => {
        // getSearchFrom already handles case conversion based on matchCase
        const searchableText = getSearchFrom(t);
        return searchableText.includes(processedSearchKey);
      }),
    ];

    tagOptions = tagOptions.filter(
      ({ value: v1 }: ITagItem) =>
        !getSelectedTagList().some(({ value: v2 }: ITagItem) => v1 === v2)
    );

    return tagOptions;
  };

  const onTagChange = (newTags: ITagItem[]): void => {
    const totalInputCount = props.inputCount?.totalCount;

    // Call callback if provided
    if (props.onTagChange) {
      props.onTagChange(newTags);
    }

    if (totalInputCount && newTags.length > totalInputCount) {
      setSearchKey('');
      setErrMessage(props.limitErrorMessage || '');
    } else {
      setSearchKey('');
      setErrMessage('');
    }
    setTagActiveIndex(-1);
  };

  const onAddTag = (tag: ITagItem): void => {
    onTagChange([...getSelectedTagList(), tag]);
  };

  const addTags = (tag: ITagItem): void => {
    const selectedTagList = getSelectedTagList();
    const matchBy = getMatchBy();

    const duplicateTags = selectedTagList.filter((selectedTag) => {
      if (matchBy === SearchBy.VALUE) {
        return selectedTag.value === tag.value;
      } else if (matchBy === SearchBy.LABEL) {
        return selectedTag.label === tag.label;
      } else if (matchBy === SearchBy.BOTH) {
        return (
          selectedTag.label === tag.label || selectedTag.value === tag.value
        );
      }
      return false;
    });

    if (duplicateTags.length === 0) {
      const newTags: ITagItem[] = [...selectedTagList, tag];
      onTagChange(newTags);
    }
  };

  const saveTag = () => {
    const currentSearchKey = searchKey();
    const currentTagActiveIndex = tagActiveIndex();

    if (!currentSearchKey) {
      return;
    }

    const tagOptions = getTagOptions();

    if (
      getAllowCustomTag() &&
      currentTagActiveIndex === -1 &&
      tagOptions.length === 0
    ) {
      if (props.validateRx && !props.validateRx.test(currentSearchKey)) {
        setErrMessage(props.invalidErrorMsg || '');
        return;
      }
      const tag: ITagItem = {
        label: currentSearchKey,
        value: currentSearchKey,
        isValid: true,
      };
      addTags(tag);
    } else if (
      tagOptions &&
      tagOptions.length > 0 &&
      currentTagActiveIndex >= 0
    ) {
      const tag: ITagItem = tagOptions[currentTagActiveIndex];
      addTags(tag);
    }
  };

  const onRemoveTag = (index: number) => {
    const newTags = [...getSelectedTagList()];
    newTags.splice(index, 1);
    onTagChange([...newTags]);
  };

  const handleInputChange = (e: Event): void => {
    const target = e.target as HTMLInputElement;
    const query = target.value;

    if (query.length > 0) {
      const lastChar = query
        .charCodeAt(query.length - 1)
        .toString() as Separators;
      if (getSeparator().includes(lastChar)) {
        return;
      }
    }

    // Call type text callback if provided
    if (props.onTypeText) {
      props.onTypeText(query);
    }

    setSearchKey(query);
    setErrMessage('');
  };

  const handleKeyPress = (e: KeyboardEvent): void => {
    const currentSearchKey = searchKey();
    const key = e.charCode.toString() as Separators;

    if (!getSeparator().includes(key) || currentSearchKey.length === 0) {
      return;
    }

    saveTag();
    e.preventDefault();
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.keyCode === 8 && searchKey().length === 0) {
      const newTags = [...getSelectedTagList()];
      newTags.pop();
      onTagChange(newTags);
    }

    const currentTagActiveIndex = tagActiveIndex();
    const tagOptions = getTagOptions();

    if (tagOptions.length > 0) {
      if (
        e.key === 'ArrowDown' &&
        currentTagActiveIndex < tagOptions.length - 1
      ) {
        setTagActiveIndex(currentTagActiveIndex + 1);
        return;
      }
      if (e.key === 'ArrowUp' && currentTagActiveIndex > 0) {
        setTagActiveIndex(currentTagActiveIndex - 1);
        return;
      }
    }
  };

  const handlePaste = (e: ClipboardEvent): void => {
    if (!getAllowCustomTag()) {
      return;
    }

    let pasteText: string = e.clipboardData?.getData('text/plain') || '';

    // Detecting newlines
    pasteText = pasteText.replace(
      /\n/g,
      String.fromCharCode(+getSeparator()[0])
    );

    // Removing the spaces
    pasteText = pasteText.replace(/\s/g, '');

    // Splitting pasted string as array based on separator prop
    getSeparator().forEach(
      (code) =>
        (pasteText = pasteText.split(String.fromCharCode(+code)).join('||'))
    );

    // Skipping the selectedTags
    const existingTags = getSelectedTagList().map((tag) => tag.value);
    const tagString: string[] = pasteText
      .split('||')
      .filter((tag) => !existingTags.includes(tag) && tag.length > 0);

    // Making array of tags from array of strings
    const pastTags: ITagItem[] = tagString.map((tag) => ({
      label: tag,
      value: tag,
      isValid: props.validateRx ? props.validateRx.test(tag) : true,
    }));

    const allTags: ITagItem[] = [...getSelectedTagList(), ...pastTags];
    onTagChange(allTags);
    e.preventDefault();
  };

  const handleSearchClose = () => {
    // Call search close callback if provided
    if (props.onSearchClose) {
      props.onSearchClose();
    }
    setSearchKey('');
  };

  // Computed values
  const errorMessage = () => errMessage() || props.customErrorMessage || '';
  const tagOptions = () => getTagOptions();
  const notFoundErrorMsg = () =>
    getNotFoundErrorMsg().replace('{{userSearch}}', searchKey());
  const selectedInputCount = () =>
    props.inputCount?.selectedCount ?? getSelectedTagList().length;
  const totalInputCount = () => props.inputCount?.totalCount;
  const inputCounter = () =>
    `Selected ${selectedInputCount().toString()}/${(totalInputCount() ?? 1).toString() || '∞'}`; // TODO: using 1 as fallback to avoid division by zero

  const shouldShowTagItems = (): boolean => {
    const currentSearchKey = searchKey();
    const currentTags = tags();
    const currentTagOptions = tagOptions();
    const predefinedTagList = props.predefinedTagList;

    return !!(
      (currentSearchKey && predefinedTagList) ||
      (currentTags &&
        currentSearchKey &&
        (!getAllowCustomTag() ||
          props.notFoundErrorMsg ||
          currentTagOptions.length > 0))
    );
  };

  const getTagItems = (): ITagItem[] => {
    if (props.predefinedTagList && props.predefinedTagList.length > 0) {
      return props.predefinedTagList;
    }
    return tagOptions();
  };

  return (
    <div class="input-group tags">
      <div class="input-group mb-0">
        <Show when={props.title}>
          <label id="tageditor-title">{props.title}</label>
        </Show>
        <Show when={props.inputCount}>
          <label id="input-count">({inputCounter()})</label>
        </Show>
      </div>
      <div
        class={`group-items input-focus-group ${
          props.enableSearchIcon ? 'tag-spacing tags-single-lin' : ''
        }`}
      >
        <Show when={props.enableSearchIcon}>
          <button class="m-1 ml-1" type="button">
            <i class="tc-icon-search" />
          </button>
        </Show>
        <ul class={`${props.enableSearchIcon ? '' : 'pl-1'} te-mw-full`}>
          <InputChips onRemoveTag={onRemoveTag} tags={getSelectedTagList()} />
          <li class={props.enableSearchIcon ? 'search-box' : ''}>
            <input
              ref={inputRef}
              type="text"
              placeholder={props.placeHolder}
              onPaste={handlePaste}
              onInput={handleInputChange}
              onKeyPress={handleKeyPress}
              onKeyDown={handleKeyDown}
              value={searchKey()}
              class={errorMessage() ? 'error' : ''}
              autocomplete="off"
              id={props.id}
              onBlur={() => {
                if (props.autoSave) {
                  saveTag();
                }
                setSearchKey('');
                setTagActiveIndex(-1);
              }}
              maxLength={props.maxLength}
              data-cy={`${props.title?.toLowerCase() ?? 'untitled'}-input`}
            />
            <div class="line" />
            <Show
              when={
                props.maxLength && inputRef?.value.length === props.maxLength
              }
            >
              <label class="error-message">
                Maximum length of {props.maxLength} characters reached
              </label>
            </Show>

            <Show when={shouldShowTagItems()}>
              <TagItems
                onAddTag={onAddTag}
                search={searchKey()}
                activeIndex={tagActiveIndex()}
                tags={getTagItems()}
                isLoading={loading()}
                notFoundErrorMsg={notFoundErrorMsg()}
                matchCase={getMatchCase()}
                enableSearchIcon={props.enableSearchIcon}
                tagListTitle={props.tagListTitle}
              />
            </Show>
          </li>
        </ul>
        <Show when={props.enableSearchIcon}>
          <button class="m-1 mr-1" type="button" onClick={handleSearchClose}>
            <i class="tc-icon-content-close" />
          </button>
        </Show>
      </div>
      <Show when={errorMessage()}>
        <label class="error-message">{errorMessage()}</label>
      </Show>
    </div>
  );
};

export default TagEditor;
